import { formatDistanceToNow, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  CheckCircle,
  Clock,
  type LucideIcon,
  PlusCircle,
  ShieldAlert,
  Unlock,
  XCircle
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { IReservationHistory } from '../../models/inventory'
import { ReservationHistoryAction, ReservationHistoryActionLabel } from '../../models/inventory'

const ACTION_CONFIG: Record<
  ReservationHistoryAction,
  {
    icon: LucideIcon
    colorClass: string
    textClass: string
  }
> = {
  [ReservationHistoryAction.CREATED]: {
    icon: PlusCircle,
    colorClass: 'bg-blue-500',
    textClass: 'text-blue-600'
  },
  [ReservationHistoryAction.AUTHORIZATION_REQUESTED]: {
    icon: ShieldAlert,
    colorClass: 'bg-amber-500',
    textClass: 'text-amber-600'
  },
  [ReservationHistoryAction.AUTHORIZED]: {
    icon: CheckCircle,
    colorClass: 'bg-green-500',
    textClass: 'text-green-600'
  },
  [ReservationHistoryAction.DECLINED]: {
    icon: XCircle,
    colorClass: 'bg-red-500',
    textClass: 'text-red-600'
  },
  [ReservationHistoryAction.EXPIRED]: {
    icon: Clock,
    colorClass: 'bg-gray-400',
    textClass: 'text-gray-500'
  },
  [ReservationHistoryAction.RELEASED]: {
    icon: Unlock,
    colorClass: 'bg-gray-400',
    textClass: 'text-gray-500'
  }
}

interface Props {
  event: IReservationHistory
  isLast: boolean
}

export function ReservationHistoryEvent({ event, isLast }: Props) {
  const config = ACTION_CONFIG[event.action]
  if (!config) return null

  const Icon = config.icon
  const label = ReservationHistoryActionLabel[event.action]
  const relativeTime = formatDistanceToNow(parseISO(event.performedAt), {
    addSuffix: true,
    locale: es
  })
  const performerName = event.performedBy?.fullname ?? 'Sistema'

  return (
    <div className="group">
      <div className="flex items-center">
        <div className="h-auto w-6 flex items-center justify-center">
          <div
            className={cn(
              'rounded-full h-5 w-5 aspect-square flex items-center justify-center',
              config.colorClass
            )}
          >
            <Icon className="h-3 w-3 stroke-white" />
          </div>
        </div>
        <div className="space-x-1 pl-2.5">
          <span className={cn('font-medium text-sm', config.textClass)}>{label}</span>
          <time className="text-muted-foreground font-medium text-xs">{relativeTime}</time>
        </div>
      </div>
      <div className="flex py-1.5">
        <div className="h-auto w-6 flex items-center justify-center">
          {!isLast && <div className="h-full w-[2px] bg-muted-foreground/30" />}
        </div>
        <div className="flex flex-col gap-1 flex-1 pl-2.5 pb-2">
          <p className="text-xs text-muted-foreground">
            por <span className="font-medium text-foreground">{performerName}</span>
          </p>
          {event.notes && (
            <p className="text-xs text-muted-foreground italic max-w-[40ch] text-pretty">
              {event.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
